import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, LoadingOverlay } from '@pletly/ui';

import { RootStackScreenProps } from '../../../../navigation/types';
import {
	splitForCurrentTeamAndRest,
	useOrganisationData,
} from '../../../view/components/useOrganisationData';
import { withTranslation } from '../../../../locales/hoc';
import { useAddMembersToTeamMutation } from './components/__generated__/addMembersToTeam.mutation.generated';
import { AddMembersToTeamModal } from './components/AddMembersToTeamModal';

export type AddMembersToTeamScreenParams = {
	organisationUuid: string;
	teamUuid: string;
	type: 'caregivers' | 'careRecipients';
};

type Props = RootStackScreenProps<'AddMembersToTeam'>;

export const AddMembersToTeam = withTranslation(
	({
		navigation: { goBack },
		route: {
			params: { organisationUuid, teamUuid, type },
		},
	}: Props) => {
		const { t } = useTranslation();
		const [addMembers, { loading: addMembersLoading }] = useAddMembersToTeamMutation();
		const { organisation, loading } = useOrganisationData({ uuid: organisationUuid });

		const handleSave = useCallback(
			async (membersIds: string[]) => {
				if (loading) return;
				if (!membersIds.length) return goBack();
				try {
					await addMembers({
						variables: {
							data: {
								teamUuid,
								membersIds,
							},
						},
					});
					Toast.show({ text1: t('toasts.success.members-added-to-team') });
					goBack();
				} catch (error) {
					console.error(error);
					Toast.show({
						type: 'error',
						text1: t('toasts.error.something-wrong'),
					});
				}
			},
			[addMembers, teamUuid, goBack, t, loading],
		);

		if (loading) {
			return <LoadingOverlay show />;
		}

		if (!organisation || !type) {
			return null;
		}

		const { currentTeam } = splitForCurrentTeamAndRest(organisation.teams, teamUuid);

		const teamMembers = currentTeam?.members[type] ?? [];
		const organisationMembers = organisation.members[type];
		const modalTitle =
			type === 'careRecipients' ? t('add-care-recipients-to-team') : t('add-caregivers-to-team');

		return (
			<AddMembersToTeamModal
				title={modalTitle}
				onClose={goBack}
				withOwnToast={false}
				visible
				loading={addMembersLoading}
				teamMembers={teamMembers}
				organisationMembers={organisationMembers}
				onSubmit={handleSave}
			/>
		);
	},
);
